import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Phone, Envelope, MapPin, Clock } from '@phosphor-icons/react';

export const Footer = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <footer 
      id="contact" 
      className="bg-[#0A0A0A] text-white"
      data-testid="footer-section"
    >
      {/* Main contact section */}
      <div className="py-12 sm:py-16 md:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div ref={ref} className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-end">
            {/* Left - Contact info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <span className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#D32F2F]">
                  Contact
                </span>
                <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4" data-testid="footer-title">
                  Besoin d'un
                  <br />
                  <span className="text-[#D32F2F]">électricien ?</span>
                </h2>
              </div>

              {/* Phone - Big CTA */}
              <a
                href="tel:0629676879"
                className="group inline-flex items-center gap-3 sm:gap-4 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold hover:text-[#D32F2F] transition-colors duration-200"
                data-testid="footer-phone"
              >
                <span className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full bg-[#D32F2F] flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <Phone weight="fill" className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white" />
                </span>
                <span>06 29 67 68 79</span>
              </a>

              {/* Other contacts */}
              <div className="space-y-4">
                <a
                  href="mailto:electricitebechameil@gmail.com"
                  className="flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors duration-200"
                  data-testid="footer-email"
                >
                  <Envelope weight="fill" className="w-5 h-5 text-[#D32F2F]" />
                  <span>electricitebechameil@gmail.com</span>
                </a>

                <div className="flex items-start gap-3 text-[#A1A1AA]" data-testid="footer-address">
                  <MapPin weight="fill" className="w-5 h-5 text-[#D32F2F] flex-shrink-0 mt-0.5" />
                  <span>415 Route de Dhuys<br />01250 Simandre-sur-Suran</span>
                </div>

                <div className="flex items-start gap-3 text-[#A1A1AA]" data-testid="footer-hours">
                  <Clock weight="fill" className="w-5 h-5 text-[#D32F2F] flex-shrink-0 mt-0.5" />
                  <div>
                    <span>Lun – Ven : 7h30 – 18h00</span>
                    <br />
                    <span className="text-xs">Dépannage en dehors des horaires sur appel</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right - Zone d'intervention */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <div>
                <h3 className="font-heading text-2xl font-bold mb-4" data-testid="footer-zone-title">
                  Zone d'intervention
                </h3>
                <p className="text-[#A1A1AA] mb-6">
                  Nous intervenons dans tout le département de l'Ain, principalement autour de :
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  'Bourg-en-Bresse',
                  'Simandre-sur-Suran',
                  'Ceyzériat',
                  'Jasseron',
                  'Treffort-Cuisiat',
                  'Polliat',
                  'Montrevel-en-Bresse',
                  'Villereversure',
                  'Grand-Corent',
                  'Drom',
                  'Ramasse',
                  'Bohas-Meyriat-Rignat',
                ].map((city) => (
                  <div 
                    key={city} 
                    className="flex items-center gap-2 text-[#A1A1AA]"
                    data-testid={`zone-${city.toLowerCase().replace(/['\s]/g, '-')}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D32F2F]" />
                    <span>{city}</span>
                  </div>
                ))}
              </div>

              <p className="text-sm text-[#A1A1AA] pt-4">
                Et toutes les communes environnantes du département de l'Ain (01)
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Legal - Left */}
            <div className="text-sm text-[#A1A1AA] text-center md:text-left">
              <p>© {new Date().getFullYear()} Électricité Béchameil. Tous droits réservés.</p>
            </div>

            {/* Credits - Right */}
            <div className="text-sm text-[#A1A1AA] text-center md:text-right">
              <p>
                Site conçu et réalisé par{' '}
                <a 
                  href="https://axeris-labs.fr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#D32F2F] transition-colors duration-200"
                >
                  Axeris Labs
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
